import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { getApi, patchApi } from '../../Services/apiService';
import {
  AdminStatCard,
  AdminSearch,
  AdminBadge,
  AdminModal,
  AdminModalFooter,
  AdminPagination,
} from '../components/AdminUi';
import Input from '../../Components/Input/Input';
import Button from '../../Components/Button/Button';
import FormattedPhone from '../../Components/FormattedPhone/FormattedPhone';
import {
  TENANT_STATUS,
  getTenantStatusLabel,
  getTenantStatusBadgeVariant,
  normalizeTenantStatus,
} from '../../constants/tenantStatus';

const defaultStats = {
  total_tenants: 0,
  active: 0,
  pending: 0,
  inactive: 0,
};

const defaultPagination = {
  currentPage: 1,
  pageSize: 10,
  totalPages: 1,
  totalItems: 0,
};

const getInitials = (name) =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();

function formatMemberSince(timestamp) {
  if (timestamp == null || timestamp === '') return '—';
  const date = new Date(Number(timestamp));
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function formatDetailLabel(key) {
  return String(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDetailValue(value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'number' && value > 1e12) {
    return formatMemberSince(value);
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

function RentalDetailsSection({ rentalDetails }) {
  if (!rentalDetails || typeof rentalDetails !== 'object') {
    return (
      <p style={{ margin: 0, color: '#7c7e93', fontSize: '14px' }}>No verified rental on file.</p>
    );
  }

  const entries = Object.entries(rentalDetails).filter(
    ([, value]) => value != null && value !== ''
  );

  if (entries.length === 0) {
    return (
      <p style={{ margin: 0, color: '#7c7e93', fontSize: '14px' }}>No verified rental on file.</p>
    );
  }

  return (
    <div className="admin-detail-grid">
      {entries.map(([key, value]) => (
        <p key={key} style={{ margin: 0 }}>
          <strong>{formatDetailLabel(key)}:</strong> {formatDetailValue(value)}
        </p>
      ))}
    </div>
  );
}

function normalizePagination(pag, stats, fallbackPageSize) {
  const pageSize = pag?.pageSize ?? fallbackPageSize ?? 10;
  const totalItems = pag?.totalItems ?? stats?.total_tenants ?? 0;
  const totalPages = pag?.totalPages ?? Math.max(1, Math.ceil(totalItems / pageSize));
  return {
    currentPage: pag?.currentPage ?? 1,
    pageSize,
    totalPages,
    totalItems,
  };
}

export default function TenantsPage() {
  const [tenantList, setTenantList] = useState([]);
  const [stats, setStats] = useState(defaultStats);
  const [pagination, setPagination] = useState(defaultPagination);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [viewMode, setViewMode] = useState(null);
  const [editData, setEditData] = useState(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [listRefreshKey, setListRefreshKey] = useState(0);

  const searchTimeoutRef = useRef(null);
  const previousDebouncedSearchRef = useRef('');

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  useEffect(() => {
    let cancelled = false;

    const fetchTenants = async () => {
      setIsLoading(true);
      try {
        const params = {
          page: pagination.currentPage,
          limit: pagination.pageSize,
        };

        if (debouncedSearch.trim()) {
          params.search = debouncedSearch.trim();
        }

        const response = await getApi('/admin/tenants', { params });
        if (cancelled) return;

        if (response?.statusCode === 200 && response?.data) {
          const nextStats = { ...defaultStats, ...response.data.stats };
          setStats(nextStats);
          setTenantList(response.data.tenants ?? []);
          setPagination((prev) =>
            normalizePagination(response.data.pagination, nextStats, prev.pageSize)
          );
        } else {
          toast.error(response?.message || 'Failed to load tenants');
          setTenantList([]);
        }
      } catch (error) {
        if (!cancelled) {
          const message =
            error?.response?.data?.message || error?.message || 'Failed to load tenants';
          toast.error(message);
          setTenantList([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchTenants();
    return () => {
      cancelled = true;
    };
  }, [pagination.currentPage, pagination.pageSize, debouncedSearch, listRefreshKey]);

  useEffect(() => {
    if (debouncedSearch !== previousDebouncedSearchRef.current && pagination.currentPage !== 1) {
      previousDebouncedSearchRef.current = debouncedSearch;
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
      return;
    }
    previousDebouncedSearchRef.current = debouncedSearch;
  }, [debouncedSearch, pagination.currentPage]);

  const closeModal = () => {
    setViewMode(null);
    setSelected(null);
    setEditData(null);
    setIsDetailLoading(false);
  };

  const openView = async (tenant) => {
    setViewMode('view');
    setSelected(null);
    setIsDetailLoading(true);

    try {
      const response = await getApi(`/admin/tenants/${tenant.id}`);
      if (response?.statusCode === 200 && response?.data) {
        setSelected(response.data);
      } else {
        toast.error(response?.message || 'Failed to load tenant details');
        setViewMode(null);
      }
    } catch (error) {
      const message =
        error?.response?.data?.message || error?.message || 'Failed to load tenant details';
      toast.error(message);
      setViewMode(null);
    } finally {
      setIsDetailLoading(false);
    }
  };

  const openEdit = (tenant) => {
    setSelected(tenant);
    setEditData({
      id: tenant.id,
      name: tenant.name ?? '',
      status: normalizeTenantStatus(tenant.status) ?? TENANT_STATUS.ACTIVE,
    });
    setViewMode('edit');
  };

  const handleSave = async () => {
    if (!editData?.id || isSaving) return;

    const name = editData.name?.trim();
    const status = normalizeTenantStatus(editData.status);

    if (!name) {
      toast.error('Name is required');
      return;
    }

    if (!status) {
      toast.error('Please select a valid account status');
      return;
    }

    setIsSaving(true);
    try {
      const response = await patchApi(`/admin/tenants/${editData.id}`, {
        name,
        status,
        delete: false,
      });

      if (response?.statusCode === 200) {
        toast.success(response?.message || 'Tenant updated successfully');
        setListRefreshKey((key) => key + 1);
        closeModal();
      } else {
        toast.error(response?.message || 'Failed to update tenant');
      }
    } catch (error) {
      const message =
        error?.response?.data?.message || error?.message || 'Failed to update tenant';
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const renderAvatar = (tenant, size = 36) => {
    if (tenant.profile_image) {
      return (
        <img
          src={tenant.profile_image}
          alt={tenant.name}
          className="admin-avatar admin-avatar--img"
          style={{ width: size, height: size }}
        />
      );
    }
    return (
      <span className="admin-avatar" style={size !== 36 ? { width: size, height: size, fontSize: size * 0.35 } : undefined}>
        {getInitials(tenant.name)}
      </span>
    );
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  return (
    <div className="admin-page admin-page--list">
      <div className="dashboard-stats">
        <AdminStatCard
          label="Total Tenants"
          value={stats.total_tenants.toLocaleString()}
          icon="/assets/images/UsersTenantDetail.svg"
        />
        <AdminStatCard
          label="Active"
          value={stats.active.toLocaleString()}
          icon="/assets/images/sidebardashboardIcon.svg"
        />
        <AdminStatCard
          label="Pending"
          value={stats.pending.toLocaleString()}
          icon="/assets/images/SearchIcon.svg"
        />
        <AdminStatCard
          label="Blocked"
          value={stats.inactive.toLocaleString()}
          icon="/assets/images/sidebarPropertyIcon.svg"
        />
      </div>

      <div className="admin-toolbar">
        <AdminSearch
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search by name, email, or phone..."
          fullWidth
        />
      </div>

      <div className="admin-table-wrap">
        <div className="admin-table-scroll">
        <table className="admin-table admin-table--compact">
          <thead>
            <tr>
              <th className="admin-table-sn">S.No.</th>
              <th>Tenant</th>
              <th>Email</th>
              <th>Status</th>
              <th className="admin-table-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className="admin-table-empty">
                  Loading tenants...
                </td>
              </tr>
            ) : tenantList.length === 0 ? (
              <tr>
                <td colSpan={5} className="admin-table-empty">
                  No tenants match your search.
                </td>
              </tr>
            ) : (
              tenantList.map((tenant, index) => (
                <tr key={tenant.id}>
                  <td className="admin-table-sn">
                    {(pagination.currentPage - 1) * pagination.pageSize + index + 1}
                  </td>
                  <td>
                    <div className="admin-user-cell">
                      {renderAvatar(tenant)}
                      <span style={{ fontWeight: 600 }}>{tenant.name}</span>
                    </div>
                  </td>
                  <td style={{ fontSize: '13px' }}>{tenant.email}</td>
                  <td>
                    <AdminBadge variant={getTenantStatusBadgeVariant(tenant.status)}>
                      {getTenantStatusLabel(tenant.status)}
                    </AdminBadge>
                  </td>
                  <td className="admin-table-actions">
                    <Button size="small" variant="outline" onClick={() => openView(tenant)}>
                      View
                    </Button>
                    <Button size="small" variant="success" onClick={() => openEdit(tenant)}>
                      Edit
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>

        {!isLoading && pagination.totalItems > 0 && (
          <AdminPagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
            pageSize={pagination.pageSize}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      <AdminModal
        open={viewMode !== null}
        onClose={closeModal}
        title={viewMode === 'view' ? 'Tenant Profile' : 'Edit Tenant'}
        footer={
          viewMode === 'edit' ? (
            <AdminModalFooter
              onCancel={closeModal}
              onConfirm={handleSave}
              confirmLabel={isSaving ? 'Saving...' : 'Save Changes'}
              confirmDisabled={isSaving}
            />
          ) : !isDetailLoading && selected ? (
            <AdminModalFooter
              onCancel={closeModal}
              onConfirm={() => openEdit(selected)}
              confirmLabel="Edit Tenant"
            />
          ) : (
            <AdminModalFooter onCancel={closeModal} onConfirm={closeModal} confirmLabel="Close" />
          )
        }
      >
        {viewMode === 'view' && isDetailLoading && (
          <p style={{ margin: 0, color: '#7c7e93' }}>Loading tenant details...</p>
        )}

        {viewMode === 'view' && !isDetailLoading && selected && (
          <>
            <div className="admin-user-cell" style={{ marginBottom: 20 }}>
              {renderAvatar(selected, 48)}
              <div>
                <p style={{ margin: 0, fontWeight: 600, fontSize: '18px' }}>{selected.name}</p>
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  <AdminBadge variant={getTenantStatusBadgeVariant(selected.status)}>
                    {getTenantStatusLabel(selected.status)}
                  </AdminBadge>
                </div>
              </div>
            </div>

            <h4
              style={{
                margin: '0 0 12px',
                fontSize: '14px',
                color: '#7c7e93',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Contact Information
            </h4>
            <div className="admin-detail-grid" style={{ marginBottom: 24 }}>
              <p style={{ margin: 0 }}>
                <strong>Phone:</strong>{' '}
                {selected.phone ? <FormattedPhone phone={selected.phone} /> : '—'}
              </p>
              <p style={{ margin: 0 }}>
                <strong>Email:</strong> {selected.email || '—'}
              </p>
              <p style={{ margin: 0 }}>
                <strong>Member since:</strong> {formatMemberSince(selected.member_since)}
              </p>
            </div>

            <h4
              style={{
                margin: '0 0 12px',
                fontSize: '14px',
                color: '#7c7e93',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Rental Details
            </h4>
            <RentalDetailsSection rentalDetails={selected.rental_details} />
          </>
        )}

        {viewMode === 'edit' && editData && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Input
              label="Full name"
              value={editData.name}
              onChange={(e) => setEditData({ ...editData, name: e.target.value })}
              disabled={isSaving}
            />
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: '14px', fontWeight: 500 }}>
                Account status
              </label>
              <select
                className="admin-select"
                style={{ width: '100%' }}
                value={editData.status}
                onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                disabled={isSaving}
              >
                <option value={TENANT_STATUS.ACTIVE}>Active</option>
                <option value={TENANT_STATUS.BLOCKED}>Blocked</option>
              </select>
            </div>
          </div>
        )}
      </AdminModal>
    </div>
  );
}
